import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api';
import { useAuth } from '../context/AuthContext';

export interface Module {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  requiredPermission?: string;
  canAccess: boolean;
}

export interface WorkspaceModulesState {
  modules: Module[];
  enabledModules: string[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateModules: (enabledModules: string[]) => Promise<void>;
  isUpdating: boolean;
}

/**
 * Hook to manage workspace modules
 * Fetches available modules and allows updating which are enabled
 */
export function useWorkspaceModules(workspaceId?: string): WorkspaceModulesState {
  const { activeWorkspace, setActiveWorkspace } = useAuth();
  const wsId = workspaceId || activeWorkspace?.id;

  const [modules, setModules] = useState<Module[]>([]);
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchModules = useCallback(async () => {
    if (!wsId) {
      setError('Workspace ID is required');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiClient.get<any>(
        `/platform/workspaces/${wsId}/modules`,
      );

      const data = response.data;
      setModules(data.modules || []);
      setEnabledModules(data.enabledModules || []);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Erro ao carregar módulos';
      setError(message);
      console.error('Error loading workspace modules:', err);
    } finally {
      setIsLoading(false);
    }
  }, [wsId]);

  const updateModules = useCallback(
    async (newEnabledModules: string[]) => {
      if (!wsId) {
        setError('Workspace ID is required');
        return;
      }

      try {
        setIsUpdating(true);
        setError(null);

        const response = await apiClient.put<any>(
          `/platform/workspaces/${wsId}/modules`,
          { enabledModules: newEnabledModules },
        );

        const data = response.data;
        setModules(data.modules || []);
        setEnabledModules(data.enabledModules || []);

        // Update activeWorkspace in AuthContext to trigger re-renders
        if (activeWorkspace) {
          const updatedWorkspace = {
            ...activeWorkspace,
            settings: {
              ...(activeWorkspace.settings || {}),
              enabledApps: data.enabledModules || [],
            },
          };
          setActiveWorkspace(updatedWorkspace);
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Erro ao atualizar módulos';
        setError(message);
        console.error('Error updating workspace modules:', err);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    [wsId, activeWorkspace, setActiveWorkspace],
  );

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  return {
    modules,
    enabledModules,
    isLoading,
    error,
    refetch: fetchModules,
    updateModules,
    isUpdating,
  };
}
